/**
 * Firmio AI identity: the product's own "F", not a generic robot.
 *
 * - `FirmioGlyph` is the bare F drawn as a lucide-style icon (currentColor),
 *   so it drops into any `icon={...}` slot (sidebar nav, empty states).
 * - `FirmioAiAvatar` is the app icon tile (dark rounded square + gradient F),
 *   used wherever the assistant gets an avatar: chat header, message bubbles,
 *   thinking indicator.
 *
 * The glyph paths are `/brand/firmio-icon-tile.svg`'s polygons scaled into
 * lucide's 24×24 box; keep them in sync if the mark ever changes.
 */
import { createLucideIcon } from 'lucide-react';

const FILLED = {
  fill: 'currentColor',
  stroke: 'currentColor',
  strokeWidth: '0.75',
  strokeLinejoin: 'round',
};

export const FirmioGlyph = createLucideIcon('FirmioGlyph', [
  ['path', { d: 'M3.7 6.65 7.77 5.51 7.77 22 3.7 20.74Z', key: 'stem', ...FILLED }],
  ['path', { d: 'M3.7 6.65 20.3 2 18.89 6 3.7 10.25Z', key: 'top', ...FILLED }],
  ['path', { d: 'M7.77 12.6 15.91 10.32 14.5 14.32 7.77 16.2Z', key: 'mid', ...FILLED }],
]);

export const FIRMIO_ICON_TILE = '/brand/firmio-icon-tile.svg';

interface FirmioAiAvatarProps {
  /** Rendered box size in px (the tile is square). */
  size?: number;
  className?: string;
}

export function FirmioAiAvatar({ size = 32, className = '' }: FirmioAiAvatarProps) {
  return (
    <img
      src={FIRMIO_ICON_TILE}
      alt="Firmio AI"
      width={size}
      height={size}
      draggable={false}
      className={`shrink-0 select-none rounded-lg ${className}`}
      style={{ width: size, height: size }}
    />
  );
}
